import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2, Send, Sparkles, ListChecks, RotateCcw, MessageSquare, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

function formatLine(text) {
  // Render **bold** inline; everything else plain.
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}

function MessageBody({ content }) {
  const lines = (content || "").split("\n");
  return (
    <div className="space-y-2 text-sm leading-relaxed">
      {lines.map((ln, i) => {
        const t = ln.trim();
        if (!t) return null;
        if (/^#{1,6}\s+/.test(t)) {
          return <p key={i} className="pt-1 font-heading text-[13px] font-semibold text-slate-900">{formatLine(t.replace(/^#{1,6}\s+/, ""))}</p>;
        }
        const bullet = /^[-•*]\s+/.test(t);
        return (
          <p key={i} className={bullet ? "flex gap-2 pl-1" : ""}>
            {bullet && <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-400" />}
            <span>{formatLine(bullet ? t.replace(/^[-•*]\s+/, "") : t)}</span>
          </p>
        );
      })}
    </div>
  );
}

function NextBestActions({ nba }) {
  if (!nba) return null;
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-6" data-testid="next-best-actions">
      <div className="flex items-center gap-2">
        <ListChecks className="h-4 w-4 text-primary" />
        <h3 className="font-heading text-lg font-semibold tracking-tight">Your Next Best Actions</h3>
      </div>
      {nba.summary && <p className="mt-2 text-sm text-slate-600">{nba.summary}</p>}
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {(nba.actions || []).map((a, i) => (
          <div key={i} className="rounded-lg border border-slate-200 p-3" data-testid={`nba-action-${i}`}>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-primary">{a.category}</div>
            <div className="mt-1 text-sm font-medium text-slate-900">{a.title}</div>
            {a.detail && <div className="mt-1 text-sm text-slate-600">{a.detail}</div>}
          </div>
        ))}
      </div>
      {nba.follow_up_questions?.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Smart questions to ask next</div>
          <ul className="mt-2 space-y-1.5">
            {nba.follow_up_questions.map((q, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" /> {q}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function AdvisorChat() {
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(true);
  const [messages, setMessages] = useState([]);
  const [starters, setStarters] = useState([]);
  const [destination, setDestination] = useState(null);
  const [nba, setNba] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [generatingNba, setGeneratingNba] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/advisor");
        setReady(data.ready);
        setMessages(data.messages || []);
        setStarters(data.starters || []);
        setDestination(data.destination);
        setNba(data.next_best_actions || null);
      } catch {
        toast.error("Could not load your advisor");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/advisor/chat", { message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The advisor is unavailable right now.");
      setMessages((m) => m.slice(0, -1)); // roll back the optimistic user message
      setInput(msg);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const generateActions = async () => {
    setGeneratingNba(true);
    try {
      const { data } = await api.post("/advisor/next-actions");
      setNba(data);
      toast.success("Your Next Best Actions are ready");
    } catch {
      toast.error("Could not generate your next steps");
    } finally {
      setGeneratingNba(false);
    }
  };

  const reset = async () => {
    try {
      await api.delete("/advisor");
      setMessages([]);
      setNba(null);
      toast.success("Started a fresh conversation");
    } catch {
      toast.error("Could not reset the conversation");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
  }

  if (!ready) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 py-16 text-center" data-testid="advisor-not-ready">
        <MessageSquare className="h-7 w-7 text-slate-400" />
        <h3 className="mt-5 font-heading text-xl font-semibold tracking-tight">Complete an assessment first</h3>
        <p className="mt-2 max-w-md text-sm text-slate-500">Your advisor reads your full report and roadmap before the first message — finish an assessment so it knows you.</p>
      </div>
    );
  }

  return (
    <div data-testid="advisor-chat">
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary"><Sparkles className="h-4 w-4 text-white" /></div>
            <div>
              <div className="text-sm font-semibold text-slate-900">Your AI Relocation Advisor</div>
              <div className="text-xs text-slate-500">{destination ? `Knows your full plan for ${destination}` : "Knows your full relocation profile"}</div>
            </div>
          </div>
          {messages.length > 0 && (
            <Button variant="ghost" size="sm" className="gap-1.5 text-slate-500" onClick={reset} data-testid="advisor-reset-btn">
              <RotateCcw className="h-3.5 w-3.5" /> New chat
            </Button>
          )}
        </div>

        <div ref={scrollRef} className="max-h-[55vh] min-h-[320px] space-y-4 overflow-y-auto px-5 py-5" data-testid="advisor-messages">
          {messages.length === 0 && (
            <div className="py-6 text-center">
              <p className="text-sm text-slate-500">Ask me anything about your move — I already know your report, scores and roadmap.</p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {starters.map((s, i) => (
                  <button key={i} onClick={() => send(s)} data-testid={`advisor-starter-${i}`}
                    className="rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 text-sm text-slate-700 transition-colors hover:border-primary hover:text-primary">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              <div
                data-testid={`advisor-message-${m.role}`}
                className={
                  m.role === "user"
                    ? "max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-4 py-2.5 text-sm text-white"
                    : "max-w-[88%] rounded-2xl rounded-bl-sm bg-slate-50 px-4 py-3 text-slate-800"
                }
              >
                {m.role === "user" ? m.content : <MessageBody content={m.content} />}
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex justify-start" data-testid="advisor-thinking">
              <div className="flex items-center gap-2 rounded-2xl rounded-bl-sm bg-slate-50 px-4 py-3 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Your advisor is thinking…
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 p-3">
          <div className="flex items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask about visas, neighborhoods, budget, schools, timelines…"
              className="min-h-[44px] resize-none"
              rows={1}
              data-testid="advisor-input"
            />
            <Button onClick={() => send()} disabled={sending || !input.trim()} className="h-11 gap-2 px-4" data-testid="advisor-send-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>

      {messages.length >= 2 && (
        <div className="mt-4 flex justify-center">
          <Button variant="outline" className="gap-2" onClick={generateActions} disabled={generatingNba} data-testid="generate-nba-btn">
            {generatingNba ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListChecks className="h-4 w-4" />}
            Generate my Next Best Actions
          </Button>
        </div>
      )}

      <NextBestActions nba={nba} />
    </div>
  );
}
